/* eslint-disable array-bracket-newline */
/**
 * Row with ticker
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ITicker, IWatchedTicker, enums } from '@kattana-io/common';
import Text from '../../atoms/Text';
import ColorPicker from './ColorPicker';
import useTickerLivePrice from './hooks/useTickerLivePrice';
import useTicker from '../../hooks/useTicker';
import { decorateExchangeName } from '../../helpers/decorators';
import NumberAnimation from '../../molecules/NumberAnimation';
import UnfavouriteStar from './UnfavouriteStar';
import NumberDisplay from '../../molecules/NumberDisplay';
import { capitalize } from '../Modal/TickersModal/helpers';
import { UpdateWatchlistItemPayload } from '../../reducers/watchlist';

export enum ExchangeStatus {
  default,
  disabled,
  enabled,
}

export enum FavStep {
  default,
  color,
  favourite,
}

interface IWatchlistLiqRowProps {
  item: ITicker | IWatchedTicker;
  actionSwitchCurrent?: (payload: ITicker) => void;
  actionRemoveWatchlist?: (id: number, callback: () => void) => void;
  actionAddWatchlist?: (
    item: ITicker,
    tag: enums.Tag,
    callback: () => void
  ) => void;
  actionUpdateWatchlist?: (
    id: number,
    payload: UpdateWatchlistItemPayload
  ) => void;
  active?: boolean;
}

const WatchlistLiqRow = ({
  item,
  actionSwitchCurrent,
  actionRemoveWatchlist,
  actionAddWatchlist,
  actionUpdateWatchlist,
  active,
}: IWatchlistLiqRowProps) => {
  const [isColorPicker, setColorPicker] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [favStep, setFavStep] = useState<FavStep>(FavStep.default);
  const [tag, setTag] = useState<enums.Tag>();

  const ref = useRef<HTMLDivElement>(null);

  const { name } = useTicker(item);
  const { price, side } = useTickerLivePrice(item);

  useEffect(() => {
    if (favStep === FavStep.color) {
      setColorPicker(true);
    }
    if (favStep === FavStep.favourite && ref.current) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionAddWatchlist && tag) {
          actionAddWatchlist(item, tag, actionCallback);
          setTag(undefined);
          setFavStep(FavStep.default);
          setColorPicker(false);
        }
      }, 150);
    }
  }, [actionAddWatchlist, favStep, item, tag]);

  const onSelect = useCallback(
    (e: any) => {
      if (actionSwitchCurrent) {
        e.stopPropagation();
        actionSwitchCurrent(item);
      }
    },
    [actionSwitchCurrent, item]
  );

  const actionCallback = () => {
    if (ref.current) {
      ref.current.classList.remove('watchlist__row--remove');
      setLoading(false);
    }
  };

  const onUnfav = useCallback(() => {
    if (ref.current && !loading) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionRemoveWatchlist) {
          actionRemoveWatchlist(item.id, actionCallback);
        }
      }, 150);
    }
  }, [actionRemoveWatchlist, item.id, loading]);

  const onFav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (!loading) {
        setFavStep(FavStep.color);
      }
    },
    [loading]
  );

  const onUpdateTag = useCallback(
    (tag: enums.Tag) => {
      if (favStep === FavStep.color) {
        setTag(tag);
        setFavStep(FavStep.favourite);
      } else if (actionUpdateWatchlist) {
        actionUpdateWatchlist(item.id, { tag });
      }
    },
    [actionUpdateWatchlist, favStep, item]
  );

  let priceColor = 'text--grey-200';
  if (side === 'buy') {
    priceColor = 'text--green-600';
  } else if (side === 'sell') {
    priceColor = 'text--red-600';
  }

  const rect = ref.current?.getBoundingClientRect();

  const exchange = capitalize(decorateExchangeName(item.exchange, item.chain));

  return (
    <div
      ref={ref}
      className={`watchlist__row ${active ? 'watchlist__row--active' : ''}`}
      onClick={onSelect}
      onContextMenu={(e) => {
        if (actionUpdateWatchlist) {
          e.preventDefault();
          setColorPicker(!isColorPicker);
        }
      }}
    >
      <div className='watchlist__row__icon_column watchlist__row__icon_column--left'>
        {actionRemoveWatchlist && (
          <UnfavouriteStar
            tag={'tag' in item && item.tag ? item.tag : enums.Tag.Blue}
            onUnfav={onUnfav}
          />
        )}
        {actionAddWatchlist && (
          <div
            className='favourite icon-star'
            data-qa='addFavourite'
            onClick={onFav}
          />
        )}
      </div>

      <div className='watchlist__row__content watchlist__row__content--no_alert'>
        <div className='watchlist__row__content__item'>
          <Text
            className='watchlist__row__exchange'
            style={{ title: exchange }}
            size={12}
          >
            {exchange}
          </Text>
          <NumberAnimation
            value={price.rawPrice}
            round={2}
            startSymbol={price.sign}
            textProps={{
              size: 14,
              className: priceColor,
            }}
          />
        </div>
        <div className='watchlist__row__content__item'>
          <Text className='text--grey-200' size={14} style={{ title: name }}>
            {name}
          </Text>
          <NumberDisplay
            value={item.liquidity}
            round={2}
            startSymbol='Liq: '
            endSymbol='$'
            textProps={{
              size: 10,
              className: 'watchlist__row--neutral',
              style: { title: `${item.liquidity}$` },
            }}
          />
          {isColorPicker && rect && (
            <ColorPicker
              top={rect.top - 80}
              left={rect.left - 5}
              onClose={() => {
                if (favStep === FavStep.color) {
                  onUpdateTag(enums.Tag.Blue);
                }
                setColorPicker(false);
              }}
              activeTag={'tag' in item ? item.tag : undefined}
              onSelect={onUpdateTag}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchlistLiqRow;
